import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/Blocks";
import { Reveal } from "@/components/Reveal";
import { CITIES, SERVICES, COUNTIES, INSIGHTS, IMAGES, comboAnchor } from "@/data/contentEngine";

const SitemapPage = () => (
  <>
    <Seo
      title="Site Directory | All Security Services & Locations | Xtreme Protection NI"
      description="Full directory of every Xtreme Protection page — 10 security services, 17 Northern Ireland locations and dedicated service pages for every city."
      path="/sitemap"
    />
    <PageHero
      eyebrow="Site Directory"
      title="Every page, one directory"
      sub="All security services, all Northern Ireland locations and every dedicated local service page."
      image={IMAGES.belfastCentre}
      testId="sitemap-hero"
    />
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-12" data-testid="sitemap-content">
      <Reveal>
        <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
          Security Services Northern Ireland
        </h2>
        <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="sitemap-services">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`sitemap-service-${s.slug}`}
                className="group flex items-center text-sm text-slate-300 transition-colors hover:text-amber-400"
              >
                <ChevronRight className="mr-1 h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
                {s.title} Northern Ireland
              </Link>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.08}>
        <h2 className="mt-14 font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
          Areas We Cover
        </h2>
        <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="sitemap-cities">
          {CITIES.map((c) => (
            <li key={c.slug}>
              <Link
                to={`/cities/${c.slug}`}
                data-testid={`sitemap-city-${c.slug}`}
                className="group flex items-center text-sm text-slate-300 transition-colors hover:text-amber-400"
              >
                <MapPin className="mr-1 h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
                Security Guards {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.06}>
        <h2 className="mt-14 font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
          Counties of Northern Ireland
        </h2>
        <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="sitemap-counties">
          {COUNTIES.map((c) => (
            <li key={c.slug}>
              <Link
                to={`/counties/${c.slug}`}
                data-testid={`sitemap-county-${c.slug}`}
                className="group flex items-center text-sm text-slate-300 transition-colors hover:text-amber-400"
              >
                <MapPin className="mr-1 h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
                Security Company {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.08}>
        <h2 className="mt-14 font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
          Security Insights & Company
        </h2>
        <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="sitemap-insights">
          <li>
            <Link to="/insights" data-testid="sitemap-insights-index" className="group flex items-center text-sm text-slate-300 transition-colors hover:text-amber-400">
              <ChevronRight className="mr-1 h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
              Security Insights Northern Ireland
            </Link>
          </li>
          <li>
            <Link to="/careers" data-testid="sitemap-careers" className="group flex items-center text-sm text-slate-300 transition-colors hover:text-amber-400">
              <ChevronRight className="mr-1 h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
              Security Jobs Northern Ireland — Careers
            </Link>
          </li>
          {INSIGHTS.map((a) => (
            <li key={a.slug}>
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`sitemap-insight-${a.slug}`}
                className="group flex items-center text-sm text-slate-400 transition-colors hover:text-amber-400"
              >
                <ChevronRight className="mr-1 h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                {a.title}
              </Link>
            </li>
          ))}
        </ul>
      </Reveal>

      {SERVICES.map((s, si) => (
        <Reveal key={s.slug} delay={0.05}>
          <h2 className="mt-14 font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400" data-testid={`sitemap-group-${s.slug}`}>
            {s.title} by City
          </h2>
          <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid={`sitemap-combos-${si}`}>
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/services/${s.slug}/${c.slug}`}
                  data-testid={`sitemap-combo-${s.slug}-${c.slug}`}
                  className="group flex items-center text-sm text-slate-400 transition-colors hover:text-amber-400"
                >
                  <ChevronRight className="mr-1 h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                  {comboAnchor(s, c)}
                </Link>
              </li>
            ))}
          </ul>
        </Reveal>
      ))}
    </section>
  </>
);

export default SitemapPage;
